import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import ru from './locales/ru.json';

// App-wide i18next setup.
//
// English is the default and the fallback to match the rest of the app, which is
// still hardcoded English. Only the MFA/2FA feature has been migrated to
// translation keys so far; the infrastructure here is intentionally generic so the
// rest of the app can adopt `useTranslation()` incrementally.
//
// The detected language is persisted to localStorage so a user who switches to
// Russian keeps it across reloads, but with `en` always winning when nothing is
// stored.
export const SUPPORTED_LANGUAGES = ['en', 'ru'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// localStorage key used by both the LanguageDetector and the `setLanguage` helper.
export const LANGUAGE_STORAGE_KEY = 'asend-lang';

// The locale files use feature keys (`mfa`, ...) at their root. i18next treats each
// top-level key under a language as a *namespace*, so the raw JSON would register a
// `mfa` namespace while `useTranslation()` looks in the default `translation`
// namespace — making every `t('mfa.…')` call return the raw key. Wrapping each
// locale under `translation` puts all keys in the default namespace so `t()` resolves.
export const resources = {
  en: { translation: en },
  ru: { translation: ru },
} as const;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    fallbackLng: 'en',
    lng: 'en',
    interpolation: {
      // React already escapes values, so i18next must not double-escape.
      escapeValue: false,
    },
    detection: {
      // Only an explicit, persisted user choice overrides the English default —
      // we intentionally do NOT fall back to the browser's `navigator` language,
      // so a Russian-locale browser still gets English unless the user opts in.
      order: ['localStorage'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
  });

// Simple language switch: changes the active language and persists the choice so it
// survives reloads (the LanguageDetector `caches: ['localStorage']` writes the key,
// and we also set it eagerly so the choice is stored even before the next detection
// pass). Use this from any future language-switcher UI:
//   import { setLanguage } from '@src/core/i18n';
//   setLanguage('ru');
export const setLanguage = (lng: SupportedLanguage): Promise<unknown> => {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
  } catch {
    // localStorage may be unavailable (private mode / SSR) — switching the
    // in-memory language is still useful, so don't fail the call.
  }
  return i18n.changeLanguage(lng);
};

export const getLanguage = (): SupportedLanguage =>
  (SUPPORTED_LANGUAGES as readonly string[]).includes(i18n.language)
    ? (i18n.language as SupportedLanguage)
    : 'en';

export default i18n;
